import React from 'react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Capture error details
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // Log the error
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Check if this is the specific React child rendering error
    if (error.message && error.message.includes('Objects are not valid as a React child')) {
      console.error('React child rendering error - likely trying to render an object directly in JSX');
      console.error('Error details:', {
        error: error,
        stack: error.stack,
        componentStack: errorInfo.componentStack
      });
    }
  }

  render() {
    if (this.state.hasError) {
      const isObjectRenderError = this.state.error?.message?.includes('Objects are not valid as a React child');
      
      return (
        <div className="min-h-screen bg-gray-50 px-4 py-16 sm:px-6 sm:py-24 md:grid md:place-items-center lg:px-8">
          <div className="mx-auto max-w-max">
            <main className="sm:flex">
              <div className="sm:ml-6">
                <div className="sm:border-l sm:border-gray-200 sm:pl-6">
                  <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
                    Something went wrong
                  </h1>
                  <p className="mt-1 text-base text-gray-500">
                    {isObjectRenderError 
                      ? 'There was an issue displaying the search results. This usually happens when the backend returns data in an unexpected format.'
                      : 'An unexpected error occurred while rendering the page.'
                    }
                  </p>
                  <div className="mt-10 flex space-x-3 sm:border-l sm:border-transparent sm:pl-6">
                    <button
                      onClick={() => {
                        this.setState({ hasError: false, error: null, errorInfo: null });
                        window.location.reload();
                      }}
                      className="inline-flex items-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                      Reload page
                    </button>
                    <button
                      onClick={() => {
                        this.setState({ hasError: false, error: null, errorInfo: null });
                      }}
                      className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                      Try again
                    </button>
                  </div>
                  {process.env.NODE_ENV === 'development' && (
                    <details className="mt-6 whitespace-pre-wrap text-sm text-gray-500">
                      <summary className="cursor-pointer font-medium text-gray-700">
                        Error details (development only)
                      </summary>
                      <div className="mt-2 max-h-60 overflow-auto rounded-md bg-gray-100 p-3">
                        <p><strong>Error:</strong> {this.state.error && this.state.error.toString()}</p>
                        <p><strong>Stack:</strong> {this.state.error && this.state.error.stack}</p>
                        <p><strong>Component Stack:</strong> {this.state.errorInfo && this.state.errorInfo.componentStack}</p>
                      </div>
                    </details>
                  )}
                </div>
              </div>
            </main>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;